import { useState } from 'react';
import { ArrowLeft, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { useMailAccountGroups, useMailAccountGroupActions } from '@/hooks/useDispatcher';
import type { MailAccountGroupItem } from '@/types/api';
import { GroupEditorModal } from './GroupEditorModal';

export default function GroupsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';

  const { data, isLoading } = useMailAccountGroups(workspaceId);
  const actions = useMailAccountGroupActions(workspaceId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MailAccountGroupItem | null>(null);

  const groups = data?.groups ?? [];

  function openCreate() {
    setEditTarget(null);
    setEditorOpen(true);
  }

  function openEdit(group: MailAccountGroupItem) {
    setEditTarget(group);
    setEditorOpen(true);
  }

  async function handleSave({ name, color }: { name: string; color: string }) {
    if (editTarget) {
      await actions.update.mutateAsync({ id: editTarget.id, name, color });
      toast.success('Groupe mis à jour');
    } else {
      await actions.create.mutateAsync({ name, color });
      toast.success('Groupe créé');
    }
    setEditorOpen(false);
  }

  async function handleDelete(group: MailAccountGroupItem) {
    if (!confirm(`Supprimer le groupe "${group.name}" ?`)) return;
    await actions.remove.mutateAsync(group.id);
    toast.success(`Groupe "${group.name}" supprimé`);
  }

  const isSaving = actions.create.isPending || actions.update.isPending;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/settings/email-accounts"
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-brand-dark"
        >
          <ArrowLeft size={14} /> Comptes Email
        </Link>
        <Button onClick={openCreate}>
          <Plus size={14} /> Nouveau groupe
        </Button>
      </div>

      <Card>
        <CardHeader
          title="Groupes de comptes email"
          subtitle="Organisez vos comptes en groupes pour les assigner à des campagnes"
        />
        {isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : groups.length === 0 ? (
          <EmptyState title="Aucun groupe créé" />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-brand-border">
              <tr>
                <th className="th">Groupe</th>
                <th className="th">Comptes</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {groups.map((g) => (
                <tr key={g.id} className="hover:bg-gray-50">
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full inline-block flex-shrink-0"
                        style={{ backgroundColor: g.color }}
                      />
                      <span className="font-medium text-brand-dark text-sm">{g.name}</span>
                    </div>
                  </td>
                  <td className="td">
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Users size={13} className="text-gray-400" />
                      {g.account_count}
                    </span>
                  </td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(g)}>
                        <Pencil size={12} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(g)}
                        loading={actions.remove.isPending}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <GroupEditorModal
        open={editorOpen}
        group={editTarget}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        loading={isSaving}
      />
    </div>
  );
}
